import { IconProp } from "@fortawesome/fontawesome-svg-core";
// The v5 solid `th` look (9 separate rounded squares) is `faGrid` in FA6 — FA6
// restyled `faTh` into a framed grid, so `faGrid` is the classic-solid glyph.
import { faGrid } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";

/** The two file-listing layouts the published-space viewer offers. */
export type SpaceView = "list" | "grid";

export type SpaceViewToggleProps = {
  view: SpaceView;
  onChange: (view: SpaceView) => void;
};

const W = 80; // whole toggle width (elongated → reads as an ellipse, not a circle)
const H = 30; // whole toggle height
const RADIUS = H / 2; // full-pill (stadium) corners
const ICON_H = 12; // shared icon height, so list and grid line up
const TRACK_BG = "#F9F9F9"; // idle half (the exposed track)
const TRACK_BORDER = "#E2E2E2"; // outline so the near-white track reads on the header
const THUMB_BG = "var(--primary-light-color)"; // active half (the sliding knob)
const ACTIVE_FG = "var(--primary-color)"; // active glyph (on the knob)
const IDLE_FG = "#E2E2E2"; // idle glyph (in the track)

/**
 * Grid / list layout switch: a single elliptical track carrying a primary-light
 * knob that slides to the active side. The idle half shows the #F9F9F9 track; the
 * active glyph is primary-colored (on the knob), the idle glyph is dimmed. Left =
 * list, right = grid. Percentage geometry keeps the knob exactly half-width
 * regardless of the track border.
 */
export function SpaceViewToggle({ view, onChange }: SpaceViewToggleProps) {
  const { t } = useTranslation();
  const isGrid = view === "grid";

  const setSide = (grid: boolean) => {
    if (grid !== isGrid) onChange(grid ? "grid" : "list");
  };

  return (
    <Tooltip
      title={isGrid ? t("View as List") : t("View as Grid")}
      placement="top"
    >
      <Box
        role="switch"
        aria-checked={isGrid}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setSide(!isGrid);
          } else if (e.key === "ArrowLeft") setSide(false);
          else if (e.key === "ArrowRight") setSide(true);
        }}
        sx={{
          position: "relative",
          boxSizing: "border-box",
          width: W,
          height: H,
          flex: "none",
          borderRadius: `${RADIUS}px`,
          backgroundColor: TRACK_BG,
          border: `1px solid ${TRACK_BORDER}`,
          cursor: "pointer",
          outline: "none",
          "&:focus-visible": { boxShadow: "0 0 0 2px var(--primary-color)" },
        }}
      >
        {/* Tap zones: tapping the exposed side slides the knob there. */}
        <Box
          onClick={() => setSide(false)}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "50%",
            height: "100%",
          }}
        />
        <Box
          onClick={() => setSide(true)}
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            width: "50%",
            height: "100%",
          }}
        />

        {/* Dimmed glyphs, one per half (behind the knob). */}
        <HalfGlyph side="left" view="list" color={IDLE_FG} />
        <HalfGlyph side="right" view="grid" color={IDLE_FG} />

        {/* Sliding knob carrying the active glyph. Animated via transform (GPU
            composited) — `left` visibly jumps when the whole listing re-renders
            on the grid⇄list switch; transform stays smooth. Click-through so
            taps reach the zones beneath. */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "50%",
            height: "100%",
            borderRadius: `${RADIUS}px`,
            backgroundColor: THUMB_BG,
            boxShadow: "0 1px 3px rgba(17,17,26,0.28)",
            pointerEvents: "none",
            transform: `translateX(${isGrid ? "100%" : "0"})`,
            transition: "transform 0.32s cubic-bezier(0.22, 1, 0.36, 1)",
            willChange: "transform",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Both active glyphs stacked; the current one fades/scales in. */}
          <KnobGlyph view="list" shown={!isGrid} />
          <KnobGlyph view="grid" shown={isGrid} />
        </Box>
      </Box>
    </Tooltip>
  );
}

/** The glyph for a layout, at a given color and the shared icon height. */
function ViewIcon({ view, color }: { view: SpaceView; color: string }) {
  return view === "list" ? (
    <ReorderIcon height={ICON_H} color={color} />
  ) : (
    <FontAwesomeIcon
      icon={faGrid as IconProp}
      style={{ fontSize: ICON_H, color }}
    />
  );
}

/** A dimmed glyph centered in one half of the track (click-through). */
function HalfGlyph({
  side,
  view,
  color,
}: {
  side: "left" | "right";
  view: SpaceView;
  color: string;
}) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        top: 0,
        left: side === "left" ? 0 : "50%",
        width: "50%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <ViewIcon view={view} color={color} />
    </Box>
  );
}

/** A knob glyph that crossfades (opacity + scale) based on `shown`. */
function KnobGlyph({ view, shown }: { view: SpaceView; shown: boolean }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: shown ? 1 : 0,
        transform: shown ? "scale(1)" : "scale(0.6)",
        transition: "opacity 0.15s ease, transform 0.15s ease",
        pointerEvents: "none",
      }}
    >
      <ViewIcon view={view} color={ACTIVE_FG} />
    </Box>
  );
}

/**
 * The list-layout glyph — four stacked bars, inlined from `reorder_black_24dp.svg`
 * (native 14×10) so it can be tinted via `color` and sized to match the grid
 * icon's height. Purely decorative (the switch carries the label).
 */
function ReorderIcon({ height, color }: { height: number; color: string }) {
  return (
    <svg
      width={(height * 14) / 10}
      height={height}
      viewBox="0 0 14 10"
      fill={color}
      aria-hidden
    >
      <path d="M0,7.143H14V5.714H0ZM0,10H14V8.571H0ZM0,4.286H14V2.857H0ZM0,0V1.429H14V0Z" />
    </svg>
  );
}
